// 抽奖转盘模块 Hooks

import { useCallback, useEffect, useMemo } from "react";
import { useRequest } from "ahooks";
import { useWheelStore, loadWheelConfig } from "../stores";
import { wheelApi } from "../api";
import type { WheelConfig, WheelUserInfo, WheelPrizeRecord } from "../types";
import { triggerPopup } from "../../../utils/popupTriggers";
import { POPUP_CONFIGS } from "../../../config/popupConfig";

/**
 * 获取转盘配置（首次使用时自动加载）
 * @returns 当前启用的转盘配置；未启用/未加载完成时为 null
 */
export function useWheelConfig(): WheelConfig | null {
  const config = useWheelStore(state => state.config);

  useEffect(() => {
    void loadWheelConfig();
  }, []);

  return config;
}

export interface WheelEntryState {
  /** 当前启用的转盘；未启用时为 null */
  wheel: WheelConfig | null;
  /** 首页主banner位入口（HOMEPAGE_MAIN_BANNER） */
  showMainBanner: boolean;
  /** 活动列表页banner入口（ACTIVITY_LIST_BANNER） */
  showListBanner: boolean;
  /** 首页右下角浮动图标入口（FLOATING_ICON，admin 侧强制开启） */
  showFloatIcon: boolean;
  /** 主banner图片 fileId（取自 displayConfig.mainBanner） */
  mainBannerImage: string;
  /** 列表banner图片 fileId（取自 displayConfig.listBanner） */
  listBannerImage: string;
  /** 浮动图标图片 fileId（取自 displayConfig.floatIcon） */
  floatIconImage: string;
}

/**
 * 转盘活动入口展示状态
 * @description 与 admin/后端一致：展示配置存于 displayConfig，activityEntry 控制各入口是否展示，
 *   对应图片非空才真正渲染。后端原样下发 displayConfig，由前端按 activityEntry 判断。
 */
export function useWheelEntry(): WheelEntryState {
  const wheel = useWheelConfig();

  return useMemo(() => {
    // /wheel/config 只返回 enabled=true 的转盘，这里再防御一次
    const active = !!wheel && wheel.enabled !== false;
    const dc = wheel?.displayConfig;
    const entry = dc?.activityEntry ?? [];
    const mainBannerImage = dc?.mainBanner ?? "";
    const listBannerImage = dc?.listBanner ?? "";
    const floatIconImage = dc?.floatIcon ?? "";
    return {
      wheel: active ? wheel : null,
      showMainBanner:
        active && entry.includes("HOMEPAGE_MAIN_BANNER") && !!mainBannerImage,
      showListBanner:
        active && entry.includes("ACTIVITY_LIST_BANNER") && !!listBannerImage,
      showFloatIcon:
        active && entry.includes("FLOATING_ICON") && !!floatIconImage,
      mainBannerImage,
      listBannerImage,
      floatIconImage,
    };
  }, [wheel]);
}

/**
 * 转盘入口统一点击处理（三个入口共用）：打开转盘活动弹窗
 */
/**
 * /wheel/config 是否已经拉回来（不论有没有转盘）。
 * 消费方用它避免「先渲染内置兜底图、配置到达后再换成后台图」的闪烁。
 */
export function useIsWheelConfigLoaded(): boolean {
  return useWheelStore(state => state.isLoaded);
}

export function useWheelEntryClick() {
  return useCallback(() => {
    triggerPopup(POPUP_CONFIGS.LUCKY_WHEEL_ACTIVITY.id);
  }, []);
}

/**
 * 用户剩余抽奖次数与积分
 * @param ready 弹窗打开后再请求，避免无谓调用
 */
export function useWheelInfo(ready: boolean) {
  return useRequest<WheelUserInfo, []>(() => wheelApi.getInfo(), {
    ready,
    refreshDeps: [ready],
  });
}

/**
 * 当前登录用户本人的中奖记录（Rewards Record 用）
 * @param wheelId 转盘 id；为空时不请求
 */
export function useWheelMyRecords(wheelId?: string) {
  return useRequest<WheelPrizeRecord[], []>(
    () => wheelApi.getMyRecords(wheelId as string),
    {
      ready: !!wheelId,
      refreshDeps: [wheelId],
    }
  );
}
